import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, Subject, throwError } from 'rxjs'
import { catchError, map, shareReplay, tap } from 'rxjs/operators'
import { ManagementApiBaseService } from '@services/api/management-api-base.service'
import { Events } from '@services/events'
import { Paginate, ofPaginate } from '@models/paginate.model'
import {
	SupplierValidationAttempt,
	SupplierValidationCreation,
	SupplierValidationSession,
	SupplierValidationSubmitRequest
} from '@models/supplier/supplier-validation.model'

/** Marca de que a sessão já foi levada à etapa de validação: vale para o login, e é apagada ao sair e ao entrar de novo. */
export const SUPPLIER_VALIDATION_SEEN_KEY = '__supplier_validation_seen'

/** Validação documental do fornecedor (spec 049), sobre `/supplier-validation` do management. */
@Injectable({
	providedIn: 'root'
})
export class SupplierValidationService extends ManagementApiBaseService<any> {
	private readonly source = 'supplier-validation'
	private session$: Observable<SupplierValidationSession> | null = null
	private last: SupplierValidationSession | null = null
	private readonly changed = new Subject<SupplierValidationSession>()

	/** Cada nova leitura da sessão, para a faixa de aviso e as telas do processo acompanharem a mudança de status. */
	readonly changes$ = this.changed.asObservable()

	constructor(
		override http: HttpClient,
		events: Events
	) {
		super(http)

		// Sair, ter a sessão expirada ou entrar de novo limpa a conta: a leitura guardada era de quem estava antes.
		events.on('accountCleared').subscribe(() => this.reset())
	}

	/** A sessão de validação, guardada em memória até a próxima mudança (envio, resultado). */
	session(force = false): Observable<SupplierValidationSession> {
		if (!this.session$ || force) {
			this.session$ = this.get<SupplierValidationSession>(`${this.source}/session`).pipe(
				map(response => new SupplierValidationSession(response)),
				tap(session => {
					this.last = session
					this.changed.next(session)
				}),
				catchError(error => {
					this.session$ = null
					return throwError(() => error)
				}),
				shareReplay(1)
			)
		}

		return this.session$
	}

	invalidate() {
		this.session$ = null
	}

	/** Esquece a sessão lida e a marca de "já levado à validação", para o próximo login começar do zero. */
	reset() {
		this.session$ = null
		this.last = null

		try {
			sessionStorage.removeItem(SUPPLIER_VALIDATION_SEEN_KEY)
		} catch {
			// Sem sessionStorage (janela privada), não há marca a apagar.
		}
	}

	/** O fornecedor da sessão é pessoa física, segundo a última leitura (falso enquanto a sessão não foi lida, para o menu não esconder nada à toa). */
	get individual(): boolean {
		return this.last?.corporate === false
	}

	create(): Observable<SupplierValidationCreation> {
		return this.post<object, SupplierValidationCreation>(`${this.source}/attempts`, {}).pipe(map(response => new SupplierValidationCreation(response)))
	}

	/** Inicia a análise (202). O resultado vem depois, pela consulta de `current`. */
	submit(id: string, request: SupplierValidationSubmitRequest): Observable<SupplierValidationAttempt> {
		return this.post<SupplierValidationSubmitRequest, SupplierValidationAttempt>(`${this.source}/attempts/${id}/submit`, request).pipe(
			map(response => new SupplierValidationAttempt(response)),
			tap(() => this.invalidate())
		)
	}

	/** A última tentativa enviada, ou nulo (204) se ainda não houve envio. */
	current(): Observable<SupplierValidationAttempt | null> {
		return this.get<SupplierValidationAttempt | null>(`${this.source}/attempts/current`).pipe(
			map(response => (response ? new SupplierValidationAttempt(response) : null))
		)
	}

	attempts(params?: HttpParams): Observable<Paginate<SupplierValidationAttempt>> {
		return this.get<Paginate<SupplierValidationAttempt>>(`${this.source}/attempts`, params).pipe(
			map(response => ofPaginate(response, SupplierValidationAttempt))
		)
	}
}
